import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { adminMe, signup } from '../api/admin';
import { PublicPlan, fetchPublicPlans } from '../api/site';
import { PLAN_FEATURE_ORDER, assistantLine, featureLine } from '../shared/planFeatureLines';
import { TRIAL_DAYS, firstChargeDate, fmtMoney, planLimitLines } from './site/planCopy';
import { MIN_PASSWORD, setKnownMinPassword } from '../components/PasswordDialogs';
import { AuthShot, AuthSplit } from './AuthLayout';

/**
 * Self-serve signup at /signup.
 *
 * The plan is chosen on the pricing page and arrives as ?plan=&interval=, so
 * this page states which plan is being started rather than making the visitor
 * remember. A free signup ends the way LoginPage does: ask /admin/me where
 * this session belongs and go there. A PAID signup ends at Stripe — the card
 * is taken before the trial starts, and the account is not on the plan until
 * Stripe says so.
 *
 * R13 (screen 05, ABC5-06): the plan chip leads the form — "{Plan} · 14 days
 * free, first charge {date} if you keep it · Change" (the date is today plus
 * the trial: an estimate, and labelled as one) — and the column beside it
 * spells out the plan and the trial terms before anything is typed.
 */

export default function SignupPage() {
  const [params] = useSearchParams();
  const planKey = params.get('plan') || 'free';
  const interval: 'month' | 'year' = params.get('interval') === 'year' ? 'year' : 'month';

  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** The catalogue's plan, with the key it was fetched for (see below). */
  const [loaded, setLoaded] = useState<{ key: string; plan: PublicPlan | null } | null>(null);
  // Two platform rules the page states (Platform › Rules), read with the
  // catalogue; the code's numbers stand in until the server has answered.
  const [rules, setRules] = useState({ trialDays: TRIAL_DAYS, minPassword: MIN_PASSWORD });
  const navigate = useNavigate();

  // The catalogue is the source of the price shown here, exactly as on the
  // pricing page — a hardcoded number on a signup form is a number that goes
  // stale the first time somebody edits a plan. Free reads it too, for the
  // limits its column lists. "Or start on the Free plan" changes the query on
  // this same page, so a plan only counts for the key it was fetched for, and
  // an answer that arrives after the key moved on is dropped.
  useEffect(() => {
    let alive = true;
    fetchPublicPlans()
      .then((r) => {
        if (!alive) return;
        setLoaded({ key: planKey, plan: r.plans.find((p) => p.key === planKey) ?? null });
        if (r.rules) {
          setRules({ trialDays: r.rules.firstTrialDays, minPassword: r.rules.minPasswordLength });
          setKnownMinPassword(r.rules.minPasswordLength);
        }
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [planKey]);
  const plan = loaded?.key === planKey ? loaded.plan : null;

  const paid = planKey !== 'free';
  const planName = paid ? (plan?.name || planKey) : (plan?.name || 'Free');
  const priceCents = plan
    ? (interval === 'year' ? plan.price_annual_cents : plan.price_monthly_cents)
    : null;
  const per = interval === 'year' ? ' / year' : ' / month';
  const firstCharge = firstChargeDate(new Date(), rules.trialDays);
  const trialDays = rules.trialDays;
  /** The other billing period for the same plan, when the catalogue sells it. */
  const otherInterval = interval === 'year' ? 'month' : 'year';
  const otherAvailable = !!plan && (otherInterval === 'year' ? plan.price_annual_cents !== null : plan.price_monthly_cents !== null);

  /** Same landing rule as LoginPage (a fresh signup is never platform staff). */
  const routeFor = (isPlatformAdmin: boolean, masquerading: boolean) =>
    (isPlatformAdmin && !masquerading ? '/platform' : '/admin');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await signup({
        company: company.trim(), name: name.trim(), email: email.trim(), password,
        planKey, interval,
      });
      // Paid: hand off to Stripe for the card. A full navigation, not a
      // route change — Checkout is Stripe's page, not ours.
      if (r.checkoutUrl) { window.location.href = r.checkoutUrl; return; }
      // The account exists but checkout could not be started. Say so, in the
      // app, rather than dropping them somewhere that implies it worked.
      if (r.checkoutError) {
        const me = await adminMe();
        navigate(routeFor(me.isPlatformAdmin, me.masquerading), {
          replace: true, state: { notice: r.checkoutError },
        });
        return;
      }
      const me = await adminMe();
      navigate(routeFor(me.isPlatformAdmin, me.masquerading), { replace: true });
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setBusy(false);
    }
  }

  const aside = (
    <>
      <span className="auth-kicker">Your plan · {planName}</span>
      {paid ? (
        <h2 className="auth-aside-title">
          {trialDays > 0 ? (
            <>
              {trialDays} days free
              {priceCents !== null && plan ? <>, then {fmtMoney(priceCents, plan.currency)}{per}</> : null}.
            </>
          ) : (
            <>{priceCents !== null && plan ? <>{fmtMoney(priceCents, plan.currency)}{per}, from today.</> : <>Charged from today.</>}</>
          )}
          {' '}Cancel from Billing any time.
        </h2>
      ) : (
        <h2 className="auth-aside-title">Free, with no card. Move up from Billing when you need more.</h2>
      )}
      {plan && (
        <ul className="auth-ticks">
          {planLimitLines(plan).slice(0, 4).map((l) => <li key={l}>{l}</li>)}
          {/* What this plan includes, in the pricing card's order — the
              assistant's allowance under the builder's, as there. */}
          {PLAN_FEATURE_ORDER.flatMap((k) => [
            featureLine(k, plan.features, plan.limits),
            ...(k === 'aiBuilder' ? [assistantLine(plan.features, plan.limits)] : []),
          ]).filter((f) => f.has).map((f) => <li key={f.text}>{f.text}</li>)}
        </ul>
      )}
      {paid && (
        <div className="auth-terms">
          {trialDays > 0 ? (
            <>
              {trialDays} days free. We take a card on the next screen so nothing breaks when the trial
              ends; your first charge would be on <b>{firstCharge}</b> if you keep it, and you can cancel
              before then from Billing.
            </>
          ) : (
            <>We take a card on the next screen and the first charge is today. You can cancel any time from Billing.</>
          )}
        </div>
      )}
      {paid && otherAvailable && (
        <Link to={`/signup?plan=${encodeURIComponent(planKey)}&interval=${otherInterval}`}>
          {otherInterval === 'year'
            ? `Switch to annual${plan!.annual_discount_pct > 0 ? ` and save ${plan!.annual_discount_pct}%` : ''}`
            : 'Switch to monthly billing'}
        </Link>
      )}
      <AuthShot shot="submissions" />
    </>
  );

  return (
    <AuthSplit aside={aside}>
      <form className="auth-stack" onSubmit={onSubmit}>
        <div className="auth-plan-chip">
          {/* The price is said here as well as in the plan column, which a
              phone does not show: nobody reaches the card without it. */}
          <span>
            <b>{planName}</b>
            {' · '}
            {paid
              ? `${priceCents !== null && plan ? `${fmtMoney(priceCents, plan.currency)}${per} · ` : ''}${trialDays > 0 ? `${trialDays} days free, first charge ${firstCharge} if you keep it` : 'charged today'}`
              : 'no card needed'}
          </span>
          <Link to="/pricing">Change</Link>
        </div>
        <h1 className="auth-title">{paid ? `Start your ${planName} trial` : 'Start on the Free plan'}</h1>
        {!paid && <p className="auth-lede">Create your account and start building forms in minutes.</p>}
        {error && <div className="auth-err" role="alert">{error}</div>}

        <div className="auth-field">
          <label htmlFor="su-name">Your name</label>
          <input id="su-name" type="text" value={name} autoFocus autoComplete="name"
            onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="auth-field">
          <label htmlFor="su-email">Work email</label>
          <input id="su-email" type="text" inputMode="email" value={email} autoComplete="username"
            placeholder="you@company.com" onChange={(e) => setEmail(e.target.value)} />
        </div>

        <div className="auth-field">
          <label htmlFor="su-company">Company</label>
          <input id="su-company" type="text" value={company} autoComplete="organization"
            onChange={(e) => setCompany(e.target.value)} />
        </div>

        <div className="auth-field">
          <label htmlFor="su-password">Password</label>
          <input id="su-password" type="password" value={password} autoComplete="new-password"
            aria-describedby="su-password-hint" onChange={(e) => setPassword(e.target.value)} />
          <div className="auth-hint" id="su-password-hint">At least {rules.minPassword} characters.</div>
        </div>

        <button className="fs-btn primary lg block" type="submit" disabled={busy}>
          {busy
            ? (paid ? 'Taking you to checkout…' : 'Creating your account…')
            : (paid ? 'Continue to card' : 'Create account')}
        </button>

        {/* Said before the button is pressed, not discovered on Stripe's page. */}
        {paid && (
          <p className="auth-fine">
            Next you&apos;ll enter a card with Stripe.{' '}
            {trialDays > 0
              ? <>Nothing is charged for {trialDays} days, and you can cancel before then from your billing page.</>
              : <>The first charge is today, and you can cancel any time from your billing page.</>}
          </p>
        )}

        <div className="auth-links">
          <span>Already have an account? <Link to="/login">Sign in</Link></span>
          {paid && <Link to="/signup">Or start on the Free plan, no card</Link>}
        </div>
      </form>
    </AuthSplit>
  );
}
